import { useEffect, useState } from "react";
import { Link2, RefreshCw, Copy, StopCircle, PlayCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, asset } from "../../lib/api";
import { fmtDate } from "../../lib/art";
import { Switch } from "../ui/switch";

const Stat = ({ label, value, sub, testid }) => (
  <div className="scroll-card p-5" data-testid={testid}>
    <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold">{label}</div>
    <div className="text-4xl font-bold text-[var(--tea)] mt-2">{value}<span className="text-lg text-stone-400 font-semibold">{sub}</span></div>
  </div>
);

const shareUrl = (t) => `${window.location.origin}/art/s/${t}`;

const NewShare = ({ childId, works, onCreated }) => {
  const [all, setAll] = useState(true);
  const [ids, setIds] = useState([]);
  const [name, setName] = useState(false);
  const [react, setReact] = useState(true);
  const create = async () => {
    if (!all && !ids.length) return toast.error("請至少選一幅作品");
    try {
      await api.post("/art/shares", { child_id: childId, kind: "gallery", work_ids: all ? [] : ids, show_nickname: name, reactions: react });
      toast.success("已建立分享連結");
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "建立失敗"); }
  };
  return (
    <div className="rounded-2xl border-2 border-[var(--line)] p-4 space-y-3" data-testid="art-share-new">
      <div className="flex flex-wrap gap-2">
        {[[true, "分享全部作品"], [false, "選擇作品"]].map(([v, l]) => (
          <button key={l} data-testid={`art-share-scope-${v ? "all" : "pick"}`} onClick={() => setAll(v)} className={`h-10 px-4 rounded-full text-sm font-bold border-2 ${all === v ? "bg-[var(--ink)] text-white border-transparent" : "bg-white border-[var(--line)]"}`}>{l}</button>
        ))}
      </div>
      {!all && (
        <div className="grid grid-cols-4 sm:grid-cols-8 gap-2" data-testid="art-share-picker">
          {works.map((w) => (
            <button key={w.work_id} data-testid={`art-share-pick-${w.work_id}`} onClick={() => setIds((s) => (s.includes(w.work_id) ? s.filter((x) => x !== w.work_id) : [...s, w.work_id]))}
              className={`rounded-xl overflow-hidden border-4 ${ids.includes(w.work_id) ? "border-[var(--vermilion)]" : "border-transparent opacity-60"}`}><img src={asset(w.image_url)} alt={w.title} className="aspect-square object-cover" /></button>
          ))}
        </div>
      )}
      <label className="flex items-center justify-between"><span>顯示孩子暱稱</span><Switch data-testid="art-share-new-name" checked={name} onCheckedChange={setName} /></label>
      <label className="flex items-center justify-between"><span>開放鼓勵按鈕（❤️ 👏 ⭐ 🎨）</span><Switch data-testid="art-share-new-react" checked={react} onCheckedChange={setReact} /></label>
      <button data-testid="art-share-create-btn" onClick={create} className="h-11 px-6 rounded-full bg-[var(--vermilion)] text-white font-bold flex items-center gap-2"><Link2 size={18} />建立小畫廊分享連結</button>
    </div>
  );
};

const ShareRow = ({ s, onChange }) => {
  const patch = async (body) => { try { onChange((await api.patch(`/art/shares/${s.share_id}`, body)).data); } catch (e) { toast.error(e?.response?.data?.detail || "失敗了"); } };
  const copy = () => navigator.clipboard.writeText(shareUrl(s.token)).then(() => toast.success("已複製連結"), () => toast(shareUrl(s.token)));
  return (
    <div className={`rounded-2xl border-2 p-4 space-y-2 ${s.active ? "border-green-300 bg-green-50/40" : "border-stone-200 bg-stone-50 opacity-80"}`} data-testid={`art-share-row-${s.share_id}`}>
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="font-bold">{s.work_ids.length ? `${s.work_ids.length} 幅作品` : "全部作品"} <span className="text-sm text-stone-500 font-normal">建立於 {fmtDate(s.created_at)}</span></div>
        <span className={`text-xs font-bold px-2 py-1 rounded-full ${s.active ? "bg-green-600 text-white" : "bg-stone-300"}`} data-testid={`art-share-status-${s.share_id}`}>{s.active ? "分享中" : "已停止"}</span>
      </div>
      {s.active && <div className="text-sm text-stone-600 break-all" data-testid={`art-share-url-${s.share_id}`}>{shareUrl(s.token)}</div>}
      <div className="flex flex-wrap gap-3 items-center text-sm">
        <label className="flex items-center gap-2">暱稱<Switch data-testid={`art-share-name-${s.share_id}`} checked={s.show_nickname} onCheckedChange={(v) => patch({ show_nickname: v })} /></label>
        <label className="flex items-center gap-2">鼓勵按鈕<Switch data-testid={`art-share-react-${s.share_id}`} checked={s.reactions} onCheckedChange={(v) => patch({ reactions: v })} /></label>
        {s.active && <button data-testid={`art-share-copy-${s.share_id}`} onClick={copy} className="h-9 px-3 rounded-full bg-white border flex items-center gap-1"><Copy size={14} />複製</button>}
        <button data-testid={`art-share-regen-${s.share_id}`} onClick={() => patch({ regenerate: true })} className="h-9 px-3 rounded-full bg-white border flex items-center gap-1"><RefreshCw size={14} />重新產生連結</button>
        <button data-testid={`art-share-toggle-${s.share_id}`} onClick={() => patch({ active: !s.active })} className="h-9 px-3 rounded-full bg-white border flex items-center gap-1">{s.active ? <><StopCircle size={14} />停止分享</> : <><PlayCircle size={14} />重新開啟</>}</button>
        <button data-testid={`art-share-delete-${s.share_id}`} onClick={() => api.delete(`/art/shares/${s.share_id}`).then(() => onChange(null))} className="h-9 px-3 rounded-full bg-white border text-red-600 flex items-center gap-1"><Trash2 size={14} />刪除</button>
      </div>
    </div>
  );
};

const GalleryShares = ({ childId, works }) => {
  const [shares, setShares] = useState([]);
  const load = () => api.get("/art/shares", { params: { child_id: childId } }).then((r) => setShares(r.data));
  useEffect(() => { load(); }, [childId]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="scroll-card p-6 space-y-4" data-testid="art-gallery-shares">
      <h2 className="text-lg font-bold">分享小畫廊</h2>
      <p className="text-sm text-stone-500">拿到連結的人不用登入就能看到孩子的作品，您可以隨時停止分享或換一個新連結。</p>
      <NewShare childId={childId} works={works} onCreated={load} />
      {shares.map((s) => <ShareRow key={s.share_id} s={s} onChange={(n) => setShares((xs) => (n ? xs.map((x) => (x.share_id === s.share_id ? n : x)) : xs.filter((x) => x.share_id !== s.share_id)))} />)}
    </div>
  );
};

export const ArtProgress = ({ a, childId }) => {
  const [works, setWorks] = useState([]);
  useEffect(() => { api.get("/art/works", { params: { child_id: childId } }).then((r) => setWorks(r.data)); }, [childId]);
  return (
    <div className="space-y-6" data-testid="art-progress">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Stat testid="art-stat-works" label="完成作品" value={a.works} sub=" 幅" />
        <Stat testid="art-stat-activities" label="完成活動" value={a.activities_done} sub={` / ${a.activities_total}`} />
        <Stat testid="art-stat-favorites" label="收藏作品" value={a.favorites} />
        <Stat testid="art-stat-gallery" label="掛在畫廊" value={a.in_gallery} />
        <Stat testid="art-stat-stars" label="畫坊星星" value={a.stars} sub=" ⭐" />
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="scroll-card p-6" data-testid="art-kinds">
          <h2 className="text-lg font-bold mb-3">作品類型</h2>
          {a.kinds.length ? a.kinds.map((k) => <div key={k.kind} className="flex justify-between py-1.5 border-b border-dashed border-[var(--line)]"><span>{k.name}</span><b>{k.count}</b></div>) : <p className="text-stone-500">還沒有作品</p>}
        </div>
        <div className="scroll-card p-6" data-testid="art-themes">
          <h2 className="text-lg font-bold mb-3">畫過的主題</h2>
          <div className="flex flex-wrap gap-2">{a.themes_used.length ? a.themes_used.map((t) => <span key={t} className="px-3 py-1 rounded-full bg-orange-50 border border-orange-200 text-sm">{t}</span>) : <p className="text-stone-500">還沒有畫過主題活動</p>}</div>
        </div>
      </div>
      {!!a.recent.length && (
        <div className="scroll-card p-6" data-testid="art-recent">
          <h2 className="text-lg font-bold mb-3">最近的作品</h2>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">{a.recent.map((w) => <div key={w.work_id}><img src={asset(w.image_url)} alt={w.title} className="rounded-xl aspect-square object-cover border" /><div className="text-xs mt-1 truncate">{w.title}</div></div>)}</div>
        </div>
      )}
      <GalleryShares childId={childId} works={works} />
    </div>
  );
};
